customElements.get('media-gallery') || customElements.define('media-gallery', class MediaGallery extends HTMLElement {
  constructor() {
    super();
    window.initScriptOnDemand(this, this.init.bind(this), 100);
  }

  init() {
    if (this.elements) return;

    this.elements = {
      liveRegion: this.querySelector('[id^="GalleryStatus"]'),
      viewer: this.querySelector('[id^="GalleryViewer"]'),
      thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
    };
    this.isQuickbuy = Boolean(this.closest('quick-add-modal'));
    this.mql = window.matchMedia('(min-width: 750px)');
    this.activeVariantScroll = this.dataset.activeVariantScroll;
    this.modelButton = this.querySelector('.product__xr-button');

    this.querySelectorAll('.product__media-toggle-play').forEach((button) => {
      button.addEventListener('click', (event) => {
        this.setActiveMedia(event.target.dataset.mediaSectionId);
      });
    });

    if (this.elements.thumbnails && !this.elements.thumbnails.slider) {
      this.elements.thumbnails.init?.();
    }

    if (this.activeVariantScroll === 'true' && !window.themeScrollComplete) {
      const activeMedia = this.elements.viewer.querySelector('.is-active');
      if (activeMedia) this.setActiveMedia(activeMedia.dataset.mediaId, true);
      window.themeScrollComplete = true;
    }

    if (!this.elements.thumbnails) return;

    const slideHandler = typeof debounce === 'function' ? debounce : (callback) => callback;
    this.elements.viewer.addEventListener('slideChanged', slideHandler(this.onSlideChanged.bind(this), 50));
    this.elements.viewer.addEventListener('slideChange', window.pauseAllMedia);

    this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
      const button = thumbnail.querySelector('button');
      if (button) button.addEventListener('click', this.setActiveMedia.bind(this, thumbnail.dataset.target));
    });

    if (this.dataset.desktopLayout === 'slider' && this.mql.matches) this.removeListSemantic();
  }

  onSlideChanged(event) {
    window.pauseAllMedia();
    if (!event.detail.currentElement || this.settingActiveMedia) return;

    const mediaId = event.detail.currentElement.dataset.mediaId;
    const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
    this.setActiveThumbnail(thumbnail);

    const media = this.findMedia(mediaId) || this.elements.viewer.querySelector('[data-media-id]');
    this.updateActiveMediaClass(media);

    const model = media.querySelector('product-model');
    if (this.modelButton && model) this.modelButton.dataset.shopifyModel3dId = model.dataset.mediaId;
  }

  findMedia(mediaId) {
    if (!mediaId) return null;
    const normalizedId = String(mediaId).replaceAll('quickadd-', '');
    return this.elements.viewer.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`) ||
      this.elements.viewer.querySelector(`[data-media-id="${CSS.escape(normalizedId)}"]`);
  }

  hydrateMedia(mediaId) {
    const normalizedId = String(mediaId).replaceAll('quickadd-', '');
    const template = this.querySelector(`template[data-media-template="${CSS.escape(normalizedId)}"]`);
    if (!template?.content?.firstElementChild) return null;

    const media = template.content.firstElementChild.cloneNode(true);
    template.replaceWith(media);
    return media;
  }

  updateActiveMediaClass(media) {
    if (!media) return;
    this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
      item.classList.remove('is-active');
      item.querySelector('button.deferred-media__poster')?.setAttribute('tabindex', '-1');
    });
    media.classList.add('is-active');
    media.querySelector('button.deferred-media__poster')?.setAttribute('tabindex', '0');
  }

  setActiveMedia(mediaId, initial = false) {
    if (!this.elements) this.init();
    if (typeof initial !== 'boolean') initial = false;

    this.settingActiveMedia = true;
    const media = this.findMedia(mediaId) || this.hydrateMedia(mediaId);
    if (!media) {
      this.settingActiveMedia = false;
      return;
    }

    const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${CSS.escape(String(mediaId))}"]`);
    const shouldAlignViewer = !this.mql.matches || this.dataset.desktopLayout === 'slider';
    const shouldScrollPage = this.dataset.desktopLayout !== 'slider' && this.mql.matches &&
      (initial || this.dataset.activeVariantScrollOnChange === 'true');

    // Collect geometry before mutating classes or ARIA state.
    const viewerLeft = shouldAlignViewer ? media.offsetLeft : 0;
    const mediaRect = shouldScrollPage ? media.getBoundingClientRect() : null;
    const mediaInView = mediaRect ? MediaGallery.rectInView(mediaRect) : true;
    const pageY = mediaRect ? mediaRect.top + window.scrollY : 0;
    let headerOffset = 0;

    if (shouldScrollPage) {
      headerOffset = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      if (headerOffset > 0) {
        headerOffset += parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--grid-desktop-horizontal-spacing-initial')) || 0;
      }
    }

    window.requestAnimationFrame(() => {
      this.updateActiveMediaClass(media);
      this.preventStickyHeader();

      if (shouldAlignViewer) media.parentElement.scrollTo({ left: viewerLeft, behavior: 'smooth' });

      if (shouldScrollPage && !mediaInView) {
        let top = pageY - headerOffset;
        if (top < 150) top = 0;
        if (this.isQuickbuy) {
          this.closest('.popup-modal__content') && media.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }

      if (thumbnail) {
        this.setActiveThumbnail(thumbnail);
        this.announceLiveRegion(media, thumbnail.dataset.mediaPosition);
      }
    });

    if (!initial) window.setTimeout(() => this.playActiveMedia(media), 600);
    window.setTimeout(() => { this.settingActiveMedia = false; }, 1000);
  }

  static rectInView(rect) {
    return rect.top >= 0 && rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth);
  }

  setActiveThumbnail(thumbnail) {
    if (!this.elements.thumbnails || !thumbnail) return;
    if (this.elements.thumbnails.classList.contains('hidden-xs') && window.matchMedia('(max-width: 749.98px)').matches) return;

    const needsScroll = this.elements.thumbnails.slider &&
      (typeof this.elements.thumbnails.isSlideVisible !== 'function' || !this.elements.thumbnails.isSlideVisible(thumbnail, 10));
    const thumbnailLeft = needsScroll ? thumbnail.offsetLeft : 0;

    this.elements.thumbnails.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
    thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

    if (needsScroll) this.elements.thumbnails.slider.scrollTo({ left: thumbnailLeft, behavior: 'smooth' });
  }

  announceLiveRegion(media, position) {
    const image = media.querySelector('.product__modal-opener--image img');
    if (!image || !this.elements.liveRegion) return;

    image.onload = () => {
      this.elements.liveRegion.setAttribute('aria-hidden', 'false');
      this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
      window.setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', 'true'), 2000);
    };
    if (image.complete) image.onload();
  }

  playActiveMedia(media) {
    window.pauseAllMedia();
    media.querySelector('.deferred-media')?.loadContent(false);
  }

  preventStickyHeader() {
    this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
    this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
  }

  removeListSemantic() {
    if (!this.elements.viewer.slider) return;
    this.elements.viewer.slider.setAttribute('role', 'presentation');
    this.elements.viewer.sliderItems.forEach((item) => item.setAttribute('role', 'presentation'));
  }
});
